// Styles
import "./groups-input.scss";
import { useContext, useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { CompactButton } from "../../../../../../../../../../shared/components/buttons/compact-button/CompactButton";
import type { ICampaignWizardForm } from "../../../../../../../../../../shared/types/form.type";
import { createDefaultAdGroup } from "../../utils/campaign-form-model";
import { AdGroupDraftValidationContext } from "../../context/ad-group-draft-validation.context";
import {
	getAdGroupNameDuplicateMessageAtIndex,
	getDescriptionValidationIssue,
	getHeadlineValidationIssue,
} from "../../utils/validate-text-fields";

function headlineDraftErrorText(
	issue: ReturnType<typeof getHeadlineValidationIssue>,
): string | null {
	if (issue === "too_long") return "Заголовок не може перевищувати 30 символів";
	if (issue === "invalid_chars") return "Заголовок містить недопустимі символи";
	if (issue === "repeated_punctuation")
		return "Заголовок містить повторювані розділові знаки чи символи";
	if (issue === "headline_question_mark")
		return "Знак «?» у заголовку заборонений";
	if (issue === "headline_exclamation")
		return "Знак «!» у заголовку можна використати лише один раз і не на початку";
	return null;
}

function descriptionDraftErrorText(
	issue: ReturnType<typeof getDescriptionValidationIssue>,
): string | null {
	if (issue === "too_long") return "Опис не може перевищувати 90 символів";
	if (issue === "invalid_chars") return "Опис містить недопустимі символи";
	if (issue === "repeated_punctuation")
		return "Опис містить повторювані розділові знаки чи символи";
	if (issue === "too_many_exclamations")
		return "Знак «!» в описі можна використати лише один раз";
	if (issue === "question_mark_position")
		return "Знак «?» в описі можна використати лише один раз і тільки в кінці";
	return null;
}

type AdGroupItemBlockProps = {
	fieldId: string;
	gi: number;
	appad: { headlines: string[]; descriptions: string[] };
	nameDuplicateError?: string;
	headlineDraft: string;
	setHeadlineDraft: (value: string) => void;
	descriptionDraft: string;
	setDescriptionDraft: (value: string) => void;
	onRemoveGroup: () => void;
};

function AdGroupItemBlock({
	fieldId,
	gi,
	appad,
	headlineDraft,
	setHeadlineDraft,
	descriptionDraft,
	setDescriptionDraft,
	onRemoveGroup,
	nameDuplicateError,
}: AdGroupItemBlockProps) {
	const draftCtx = useContext(AdGroupDraftValidationContext);
	const { setValue, getValues, register } =
		useFormContext<ICampaignWizardForm>();

	const headlinesPath =
		`campaign_context.adgroups.${gi}.appads.0.headlines` as const;
	const descriptionsPath =
		`campaign_context.adgroups.${gi}.appads.0.descriptions` as const;

	useEffect(() => {
		return () => {
			draftCtx?.reportHeadlineDraftBlocking(fieldId, false);
			draftCtx?.reportDescriptionDraftBlocking(fieldId, false);
		};
	}, [draftCtx, fieldId]);

	const headlineErr = headlineDraftErrorText(
		getHeadlineValidationIssue(headlineDraft),
	);
	const descErr = descriptionDraftErrorText(
		getDescriptionValidationIssue(descriptionDraft),
	);

	return (
		<div className='group-item'>
			<div className='group-item__header'>
				<div className='group-item__header_title'>
					Група {gi + 1}
					<span className='group-item__header_title_required'>
						{gi === 0 ? " (обовʼязково)" : ""}
					</span>
				</div>
				{gi > 0 && (
					<div className='added-item__remove-button' onClick={onRemoveGroup}>
						<img src='/img/icons/close.png' alt='delete' />
					</div>
				)}
			</div>
			<div className='group-item__body'>
				<div className='group-item__name-row'>
					<div className='group-item__section-label'>
						Назва (не обов'язково)
					</div>
					<input
						type='text'
						className='group-item__input'
						{...register(`campaign_context.adgroups.${gi}.name`)}
						aria-invalid={nameDuplicateError ? true : undefined}
					/>
				</div>
				{nameDuplicateError && (
					<div className='group-item__error name-duplicate'>
						Назва групи повинна бути унікальною
					</div>
				)}
				<div className='group-item__section'>
					<div className='group-item__section-label'>
						Заголовки - headlines (рекомендовано 3 або більше)
					</div>
					{appad.headlines.length > 0 && (
						<div className='group-item__chips'>
							{appad.headlines.map((headline: string, hi: number) => (
								<div key={hi} className='added-item'>
									<div className='added-item__name'>{headline}</div>
									<div
										className='added-item__remove-button'
										onClick={() => {
											const list = [...(getValues(headlinesPath) ?? [])];
											list.splice(hi, 1);
											setValue(headlinesPath, list, {
												shouldDirty: true,
											});
										}}>
										<img src='/img/icons/close.png' alt='delete' />
									</div>
								</div>
							))}
						</div>
					)}
					<div className='group-item__input-row'>
						<input
							type='text'
							className='group-item__input'
							value={headlineDraft}
							onChange={(e) => {
								const v = e.target.value;
								setHeadlineDraft(v);
								const issue = getHeadlineValidationIssue(v);
								draftCtx?.reportHeadlineDraftBlocking(
									fieldId,
									issue !== null && issue !== "empty",
								);
							}}
							onKeyDown={(e) => {
								const issue = getHeadlineValidationIssue(headlineDraft);
								if (issue !== null) return;
								const cur = headlineDraft.trim();
								if (e.key === "Enter" && cur) {
									const list = [...(getValues(headlinesPath) ?? [])];
									setValue(headlinesPath, [...list, cur], {
										shouldDirty: true,
									});
									setHeadlineDraft("");
									draftCtx?.reportHeadlineDraftBlocking(fieldId, false);
								}
							}}
						/>
						<CompactButton
							children='Додати'
							type='button'
							theme='default'
							className='small'
							unactive={getHeadlineValidationIssue(headlineDraft) !== null}
							onClick={() => {
								const issue = getHeadlineValidationIssue(headlineDraft);
								if (issue !== null) return;
								const val = headlineDraft.trim();
								if (!val) return;
								const list = [...(getValues(headlinesPath) ?? [])];
								setValue(headlinesPath, [...list, val], {
									shouldDirty: true,
								});
								setHeadlineDraft("");
								draftCtx?.reportHeadlineDraftBlocking(fieldId, false);
							}}
							iconLeft={<img src='/img/icons/add.png' alt='add' />}
						/>
					</div>
					{headlineErr && (
						<div className='group-item__error'>{headlineErr}</div>
					)}
				</div>
				<div className='group-item__section'>
					<div className='group-item__section-label'>
						Описи - descriptions (рекомендовано 2 або більше)
					</div>
					{appad.descriptions.length > 0 && (
						<div className='group-item__chips'>
							{appad.descriptions.map((desc: string, di: number) => (
								<div key={di} className='added-item'>
									<div className='added-item__name'>{desc}</div>
									<div
										className='added-item__remove-button'
										onClick={() => {
											const list = [...(getValues(descriptionsPath) ?? [])];
											list.splice(di, 1);
											setValue(descriptionsPath, list, {
												shouldDirty: true,
											});
										}}>
										<img src='/img/icons/close.png' alt='delete' />
									</div>
								</div>
							))}
						</div>
					)}
					<div className='group-item__input-row'>
						<input
							type='text'
							className='group-item__input'
							value={descriptionDraft}
							onChange={(e) => {
								const v = e.target.value;
								setDescriptionDraft(v);
								const issue = getDescriptionValidationIssue(v);
								draftCtx?.reportDescriptionDraftBlocking(
									fieldId,
									issue !== null && issue !== "empty",
								);
							}}
							onKeyDown={(e) => {
								const issue = getDescriptionValidationIssue(descriptionDraft);
								if (issue !== null) return;
								const cur = descriptionDraft.trim();
								if (e.key === "Enter" && cur) {
									const list = [...(getValues(descriptionsPath) ?? [])];
									setValue(descriptionsPath, [...list, cur], {
										shouldDirty: true,
									});
									setDescriptionDraft("");
									draftCtx?.reportDescriptionDraftBlocking(fieldId, false);
								}
							}}
						/>
						<CompactButton
							children='Додати'
							type='button'
							theme='default'
							className='small'
							unactive={
								getDescriptionValidationIssue(descriptionDraft) !== null
							}
							onClick={() => {
								const issue = getDescriptionValidationIssue(descriptionDraft);
								if (issue !== null) return;
								const val = descriptionDraft.trim();
								if (!val) return;
								const list = [...(getValues(descriptionsPath) ?? [])];
								setValue(descriptionsPath, [...list, val], {
									shouldDirty: true,
								});
								setDescriptionDraft("");
								draftCtx?.reportDescriptionDraftBlocking(fieldId, false);
							}}
							iconLeft={<img src='/img/icons/add.png' alt='add' />}
						/>
					</div>
					{descErr && <div className='group-item__error'>{descErr}</div>}
				</div>
			</div>
		</div>
	);
}

export const GroupsInput = () => {
	const { control, watch } = useFormContext<ICampaignWizardForm>();
	const { fields, append, remove, replace } = useFieldArray({
		control,
		name: "campaign_context.adgroups",
	});

	useEffect(() => {
		if (fields.length === 0) {
			replace([createDefaultAdGroup()]);
		}
	}, [fields.length, replace]);

	const [headlineInputs, setHeadlineInputs] = useState<Record<number, string>>(
		{},
	);
	const [descriptionInputs, setDescriptionInputs] = useState<
		Record<number, string>
	>({});

	const watchedAdgroups = watch("campaign_context.adgroups");

	return (
		<div className='form__fields_field-item field-item groups-input'>
			<div className='field-item__title'>Ad groups</div>
			<div className='field-item__add-groups-wrapper'>
				{fields.map((field, gi) => {
					const adgroups = watchedAdgroups ?? [];
					const appad = adgroups[gi]?.appads?.[0] ?? {
						headlines: [] as string[],
						descriptions: [] as string[],
					};
					return (
						<AdGroupItemBlock
							key={field.id}
							fieldId={field.id}
							gi={gi}
							appad={appad}
							nameDuplicateError={getAdGroupNameDuplicateMessageAtIndex(
								adgroups,
								gi,
							)}
							headlineDraft={headlineInputs[gi] ?? ""}
							setHeadlineDraft={(value) =>
								setHeadlineInputs((prev) => ({ ...prev, [gi]: value }))
							}
							descriptionDraft={descriptionInputs[gi] ?? ""}
							setDescriptionDraft={(value) =>
								setDescriptionInputs((prev) => ({ ...prev, [gi]: value }))
							}
							onRemoveGroup={() => {
								if (fields.length <= 1) return;
								remove(gi);
							}}
						/>
					);
				})}
			</div>
			<CompactButton
				children='Додати групу'
				type='button'
				theme='default'
				className='full-width'
				onClick={() => append(createDefaultAdGroup())}
				iconLeft={<img src='/img/icons/add.png' alt='add' />}
			/>
		</div>
	);
};
